const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const FRONTEND_SECRET = process.env.REACT_APP_FRONTEND_SECRET || '';

function getAuthToken(): string | null {
  return localStorage.getItem('@sistema-up:token');
}

async function apiRequest(endpoint: string, options?: RequestInit): Promise<any> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Frontend-Secret': FRONTEND_SECRET,
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: { ...headers, ...options?.headers },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export interface SessaoAtiva {
  id: string;
  usuario_id: string;
  usuario_nome: string;
  usuario_login: string;
  filial_atual: string | null;
  ip_address: string | null;
  user_agent: string | null;
  data_login: string;
  ultima_atividade: string;
  status: string;
}

export interface TentativaLogin {
  id: string;
  usuario_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  sucesso: boolean;
  criado_em: string;
  usuarios?: { nome: string; email: string; login: string } | null;
}

export interface ContaBloqueada {
  id: string;
  usuario_id: string;
  motivo: string;
  tipo_bloqueio: string;
  bloqueado_em: string;
  desbloqueado_em: string | null;
  desbloqueado_por: string | null;
  usuarios?: { nome: string; email: string; login: string } | null;
}

export interface LogUsuario {
  id: string;
  usuario_id: string;
  usuario_nome: string;
  usuario_login: string;
  acao: string;
  modulo: string;
  descricao: string;
  dados_anteriores: any;
  dados_novos: any;
  ip_address: string | null;
  user_agent: string | null;
  data_acao: string;
  filial_atual: string | null;
}

export interface OverviewMonitoramento {
  usuarios_online: SessaoAtiva[];
  total_online: number;
  tentativas_falhas_24h: TentativaLogin[];
  total_tentativas_falhas_24h: number;
  tentativas_falhas_1h: number;
  tentativas_sucesso_24h: number;
  contas_bloqueadas: ContaBloqueada[];
  total_contas_bloqueadas: number;
  logs_recentes: LogUsuario[];
  total_usuarios: number;
  usuarios_ativos: number;
  usuarios_inativos: number;
  tentativas_por_ip: Record<string, number>;
  tentativas_por_usuario: Record<string, number>;
  status_banco: string;
  latencia_banco: number;
  timestamp: string;
}

export interface PanoramaData {
  performance: {
    api_latency_ms: number;
    node_uptime_horas: number;
    node_uptime_minutos: number;
    node_version: string;
    memoria_mb: number;
    status_banco: string;
    latencia_banco_ms: number;
  };
  solicitacoes: {
    pendentes: number;
    aprovadas: number;
    reprovadas: number;
    reagendadas: number;
    total: number;
  };
  cargas: {
    em_andamento: number;
    finalizadas: number;
    canceladas: number;
    total: number;
  };
  visitas: {
    pendentes: number;
    aprovadas: number;
    finalizadas: number;
    total: number;
  };
  documentos: {
    total: number;
    recentes_24h: number;
    uploads_sucesso_7d: number;
    uploads_falha_7d: number;
    uploads_recentes: LogUsuario[];
  };
  notificacoes: {
    total: number;
    nao_lidas: number;
  };
  pedidos: {
    total: number;
    recentes_7d: number;
  };
  seguranca: {
    captchas_gerados_24h: number;
    solicitacoes_acesso_pendentes: number;
    total_solicitacoes_acesso: number;
    logs_erro_7d: number;
    logs_erro_detalhes: LogUsuario[];
    delecoes_7d: number;
    delecoes_detalhes: LogUsuario[];
  };
  questionarios_fornecedor: number;
  filiais_ativas: number;
  timestamp: string;
}

class MonitoramentoService {
  async buscarOverview(): Promise<OverviewMonitoramento> {
    const result = await apiRequest('/monitoramento/overview');
    return result.data;
  }

  async buscarSessoes(): Promise<SessaoAtiva[]> {
    const result = await apiRequest('/monitoramento/sessoes');
    return result.data;
  }

  async buscarTentativasLogin(apenasFalhas: boolean = false, limite: number = 50): Promise<TentativaLogin[]> {
    const params = new URLSearchParams();
    if (apenasFalhas) params.set('falhas', 'true');
    params.set('limite', limite.toString());
    const result = await apiRequest(`/monitoramento/tentativas-login?${params}`);
    return result.data;
  }

  async buscarContasBloqueadas(): Promise<ContaBloqueada[]> {
    const result = await apiRequest('/monitoramento/contas-bloqueadas');
    return result.data;
  }

  async desbloquearConta(usuarioId: string): Promise<void> {
    await apiRequest(`/monitoramento/desbloquear/${usuarioId}`, { method: 'POST' });
  }

  async buscarLogsErros(limite: number = 50): Promise<LogUsuario[]> {
    const result = await apiRequest(`/monitoramento/logs-erros?limite=${limite}`);
    return result.data;
  }

  async buscarPanorama(): Promise<PanoramaData> {
    const result = await apiRequest('/monitoramento/panorama');
    return result.data;
  }
}

export default new MonitoramentoService();
